import { match } from 'ts-pattern'
import { useAnimation } from 'framer-motion'
import { useInView } from 'react-intersection-observer'
import * as React from 'react'

import { MotionBox } from '../common/MotionBox'

export type ScrollRevealProps = React.ComponentPropsWithoutRef<typeof MotionBox> & {
  threshold?: number
  triggerOnce?: number
  origin?: 'top' | 'bottom' | 'left' | 'right'
  distance?: number
  duration?: number
}

const ScrollReveal: React.FC<ScrollRevealProps> = ({
  threshold = 0.5,
  triggerOnce = true,
  origin = 'bottom',
  distance = 50,
  duration = 1.0,
  children,
  ...others
}) => {
  const { inView, ref } = useInView({ threshold: threshold, triggerOnce: triggerOnce })
  const animation = useAnimation()

  const motionVariants = React.useMemo(() => {
    const offset = match(origin)
      .with('top', () => ({ x: 0, y: -distance }))
      .with('bottom', () => ({ x: 0, y: distance }))
      .with('left', () => ({ x: -distance, y: 0 }))
      .with('right', () => ({ x: distance, y: 0 }))
      .exhaustive()

    return {
      hidden: { opacity: 0, ...offset },
      visible: {
        opacity: 1,
        x: 0,
        y: 0,
        transition: {
          type: 'tween',
          ease: 'easeOut',
          duration: duration,
        },
      },
    }
  }, [origin, duration])

  React.useEffect(async () => {
    if (inView) {
      await animation.start('visible')
    }
  }, [animation, inView])

  return (
    <>
      <MotionBox ref={ref} variants={motionVariants} animate={animation} initial="hidden" {...others}>
        {children}
      </MotionBox>
    </>
  )
}

export default ScrollReveal
